/**
 * STAGE 01 — LOCALIZE (port of docloc.py).
 *
 * Find the document as a 4-corner quad and perspective-warp it. Two detectors,
 * tried in order: (1) edge/contour quad, (2) bright/low-saturation foreground
 * min-area-rect. If neither is confident, KEEP THE FULL FRAME — never crop to a
 * sub-region of the document.
 *
 * Thresholds match docloc.py exactly:
 *   quad area ≥ 0.18·frame, aspect ≤ 4, rectangularity ≥ 0.80,
 *   quad-score gate ≥ 0.30, foreground gate ≥ 0.35.
 */
import { ImagePixels } from '../../core/ports';
import { cloneImage, resizeImage, toGray, toHsv, createMask } from '../core/image';
import {
  Point,
  polygonArea,
  arcLength,
  approxPolyDP,
  isConvex,
  minAreaRect,
  boxPoints,
} from '../core/geometry';
import { gaussianBlur, canny } from '../core/edges';
import { dilate, close, open } from '../core/morphology';
import { largestBlob } from '../core/contour';
import { fourPointWarp } from '../core/warp';

export type LocalizeMethod = 'quad' | 'fg' | 'frame';

/** Port of _quad_score: area-fraction × rectangularity, 0 if any guard fails. */
function quadScore(quad: Point[], frameArea: number): number {
  const area = polygonArea(quad);
  if (area < 0.18 * frameArea) return 0;
  const rect = minAreaRect(quad);
  const w = rect.w, h = rect.h;
  if (w < 1 || h < 1) return 0;
  const ar = Math.max(w, h) / Math.min(w, h);
  if (ar > 4.0) return 0;
  const rectangularity = area / (w * h);
  if (rectangularity < 0.8) return 0;
  return (area / frameArea) * rectangularity;
}

/** Port of _detect_edges. Returns the quad in full-image coords + its score. */
function detectEdges(img: ImagePixels): { quad: Point[] | null; score: number } {
  const H = img.height, W = img.width;
  const scale = 1000 / Math.max(H, W);
  const small = resizeImage(img, W * scale, H * scale);
  const gray = toGray(small);
  // bilateralFilter(9,60,60) → Gaussian approx (see edges.ts fidelity note).
  const blurred = gaussianBlur(gray, small.width, small.height);
  let edges = canny(blurred, small.width, small.height, 40, 120);
  edges = dilate(edges, 3, 2);

  // Largest external blob isn't enough (we need the top-8 by area); but the
  // pipeline only warps from a convex 4-gon. We approximate findContours' "top 8
  // contours" by repeatedly extracting and removing the largest blob.
  const fa = small.width * small.height;
  let best: Point[] | null = null;
  let bestS = 0;
  const work = createMask(small.width, small.height);
  work.data.set(edges.data);
  for (let iter = 0; iter < 8; iter++) {
    const blob = largestBlob(work);
    if (!blob || blob.area < 4) break;
    // Erase this blob so the next iteration finds the next-largest.
    for (let i = 0; i < blob.labelMask.length; i++) if (blob.labelMask[i]) work.data[i] = 0;
    const contour = blob.boundary;
    if (contour.length < 4) continue;
    const peri = arcLength(contour, true);
    const approx = approxPolyDP(contour, 0.02 * peri);
    if (approx.length === 4 && isConvex(approx)) {
      const s = quadScore(approx, fa);
      if (s > bestS) {
        best = approx;
        bestS = s;
      }
    }
  }
  if (!best) return { quad: null, score: 0 };
  return { quad: best.map((p) => ({ x: p.x / scale, y: p.y / scale })), score: bestS };
}

/** Port of _detect_foreground. */
function detectForeground(img: ImagePixels): { quad: Point[] | null; score: number } {
  const H = img.height, W = img.width;
  const scale = 1000 / Math.max(H, W);
  const small = resizeImage(img, W * scale, H * scale);
  const { s, v } = toHsv(small);
  const mask = createMask(small.width, small.height);
  for (let i = 0; i < mask.data.length; i++) {
    mask.data[i] = v[i] > 120 && s[i] < 110 ? 255 : 0;
  }
  let m = close(mask, 15);
  m = open(m, 9);
  const blob = largestBlob(m);
  if (!blob) return { quad: null, score: 0 };
  const fa = small.width * small.height;
  const box = boxPoints(minAreaRect(blob.boundary));
  const sScore = quadScore(box, fa);
  if (sScore === 0) return { quad: null, score: 0 };
  return { quad: box.map((p) => ({ x: p.x / scale, y: p.y / scale })), score: sScore };
}

/**
 * Port of localize(). Returns the rectified image + method. Never crops to a
 * sub-region of the document: falls back to the full frame.
 */
export function localize(img: ImagePixels): { image: ImagePixels; method: LocalizeMethod } {
  const { quad, score } = detectEdges(img);
  if (quad && score >= 0.3) {
    const w = fourPointWarp(img, quad);
    if (w) return { image: w, method: 'quad' };
  }
  const fg = detectForeground(img);
  if (fg.quad && fg.score >= 0.35) {
    const w = fourPointWarp(img, fg.quad);
    if (w) return { image: w, method: 'fg' };
  }
  return { image: cloneImage(img), method: 'frame' };
}
